"use client"

import * as React from "react"
import { ScrollArea, ScrollBar } from "@workspace/ui/components/scroll-area"
import { cn } from "@workspace/ui/lib/utils"
import { UIBox } from "./ui-box"

// ============================================================================
// UIScrollArea Types
// ============================================================================

export interface UIScrollAreaProps extends React.ComponentProps<typeof ScrollArea> {
  className?: string
  children: React.ReactNode
  height?: string | number
  maxHeight?: string | number
  orientation?: "vertical" | "horizontal" | "both"
  // 접근성 속성
  "aria-label"?: string
  "aria-labelledby"?: string
  "aria-describedby"?: string
  tabIndex?: number
  role?: string
}

// ============================================================================
// UIScrollArea Component
// ============================================================================

export const UIScrollArea = React.forwardRef<
  React.ComponentRef<typeof ScrollArea>,
  UIScrollAreaProps
>(({
  className,
  children,
  height,
  maxHeight,
  orientation = "vertical",
  style,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledby,
  "aria-describedby": ariaDescribedby,
  tabIndex = 0,
  role = "region",
  ...props
}, ref) => {
  const scrollAreaStyle = {
    height,
    maxHeight,
    ...style
  }

  // 접근성 속성 구성
  const accessibilityProps = {
    "aria-label": ariaLabel || "스크롤 가능한 영역",
    "aria-labelledby": ariaLabelledby,
    "aria-describedby": ariaDescribedby,
    tabIndex,
    role
  }

  return (
    <ScrollArea
      ref={ref}
      className={cn("w-full", className)}
      style={scrollAreaStyle}
      {...accessibilityProps}
      {...props}
    >
      {children}
      {(orientation === "horizontal" || orientation === "both") && (
        <ScrollBar orientation="horizontal" />
      )}
    </ScrollArea>
  )
})

UIScrollArea.displayName = "UIScrollArea"

// ============================================================================
// Preset Scroll Area Components
// ============================================================================

export interface UIScrollAreaSidebarProps extends Omit<UIScrollAreaProps, 'height' | 'maxHeight'> {
  sidebarHeight?: string
}

export const UIScrollAreaSidebar = React.forwardRef<
  React.ComponentRef<typeof ScrollArea>,
  UIScrollAreaSidebarProps
>(({ sidebarHeight = "calc(100vh - 4rem)", className, ...props }, ref) => {
  return (
    <UIScrollArea
      ref={ref}
      height={sidebarHeight}
      className={cn("border-r", className)}
      {...props}
    />
  )
})

UIScrollAreaSidebar.displayName = "UIScrollAreaSidebar"

export interface UIScrollAreaContentProps extends Omit<UIScrollAreaProps, 'height'> {
  contentHeight?: string
}

export const UIScrollAreaContent = React.forwardRef<
  React.ComponentRef<typeof ScrollArea>,
  UIScrollAreaContentProps
>(({ contentHeight = "400px", className, ...props }, ref) => {
  return (
    <UIScrollArea
      ref={ref}
      height={contentHeight}
      className={cn(
        "rounded-md p-4",
        "bg-background/60 dark:bg-gray-900/40",
        "border border-gray-200 dark:border-gray-800",
        "ring-1 ring-white/10 dark:ring-white/5",
        "shadow-sm shadow-gray-950/5 dark:shadow-black/20",
        className
      )}
      {...props}
    />
  )
})

UIScrollAreaContent.displayName = "UIScrollAreaContent"

export interface UIScrollAreaTableProps extends Omit<UIScrollAreaProps, 'orientation'> {
  tableHeight?: string
  enableHorizontalScroll?: boolean
}

export const UIScrollAreaTable = React.forwardRef<
  React.ComponentRef<typeof ScrollArea>,
  UIScrollAreaTableProps
>(({
  tableHeight = "600px",
  enableHorizontalScroll = true,
  className,
  ...props
}, ref) => {
  return (
    <UIScrollArea
      ref={ref}
      height={tableHeight}
      orientation={enableHorizontalScroll ? "both" : "vertical"}
      className={cn(
        "rounded-md",
        "bg-background/40 dark:bg-gray-900/30",
        "border border-gray-200 dark:border-gray-800",
        "ring-1 ring-white/10 dark:ring-white/5",
        "shadow-sm shadow-gray-950/5 dark:shadow-black/20",
        className
      )}
      {...props}
    />
  )
})

UIScrollAreaTable.displayName = "UIScrollAreaTable"

export interface UIScrollAreaChatProps extends Omit<UIScrollAreaProps, 'height'> {
  chatHeight?: string
  autoScrollToBottom?: boolean
}

export const UIScrollAreaChat = React.forwardRef<
  React.ComponentRef<typeof ScrollArea>,
  UIScrollAreaChatProps
>(({
  chatHeight = "500px",
  autoScrollToBottom = true,
  className,
  children,
  ...props
}, ref) => {
  const scrollRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    if (autoScrollToBottom && scrollRef.current) {
      const scrollElement = scrollRef.current.querySelector('[data-slot="scroll-area-viewport"]') as HTMLElement
      if (scrollElement) {
        scrollElement.scrollTop = scrollElement.scrollHeight
      }
    }
  }, [children, autoScrollToBottom])

  return (
    <UIBox ref={scrollRef}>
      <UIScrollArea
        ref={ref}
        height={chatHeight}
        className={cn(
          "rounded-md p-4",
          "bg-muted/40 dark:bg-gray-900/30",
          "border border-gray-200 dark:border-gray-800",
          "ring-1 ring-white/10 dark:ring-white/5",
          "shadow-sm shadow-gray-950/5 dark:shadow-black/20",
          className
        )}
        {...props}
      >
        {children}
      </UIScrollArea>
    </UIBox>
  )
})

UIScrollAreaChat.displayName = "UIScrollAreaChat"

export interface UIScrollAreaCodeProps extends Omit<UIScrollAreaProps, 'orientation'> {
  codeHeight?: string
  showLineNumbers?: boolean
}

export const UIScrollAreaCode = React.forwardRef<
  React.ComponentRef<typeof ScrollArea>,
  UIScrollAreaCodeProps
>(({
  codeHeight = "300px",
  showLineNumbers = false,
  className,
  children,
  ...props
}, ref) => {
  return (
    <UIScrollArea
      ref={ref}
      height={codeHeight}
      orientation="both"
      className={cn(
        "rounded-md font-mono text-sm",
        "bg-muted/50 dark:bg-gray-900/40",
        "border border-gray-200 dark:border-gray-800",
        "ring-1 ring-white/10 dark:ring-white/5",
        "shadow-sm shadow-gray-950/5 dark:shadow-black/20",
        showLineNumbers && "pl-12",
        className
      )}
      {...props}
    >
      <pre className="p-4 whitespace-pre">
        {children}
      </pre>
    </UIScrollArea>
  )
})

UIScrollAreaCode.displayName = "UIScrollAreaCode"

export interface UIScrollAreaListProps extends Omit<UIScrollAreaProps, 'height'> {
  listHeight?: string
  itemSpacing?: boolean
}

export const UIScrollAreaList = React.forwardRef<
  React.ComponentRef<typeof ScrollArea>,
  UIScrollAreaListProps
>(({
  listHeight = "350px",
  itemSpacing = true,
  className,
  children,
  ...props
}, ref) => {
  return (
    <UIScrollArea
      ref={ref}
      height={listHeight}
      className={cn(
        "rounded-md",
        "bg-background/50 dark:bg-gray-900/40",
        "border border-gray-200 dark:border-gray-800",
        "ring-1 ring-white/10 dark:ring-white/5",
        "shadow-sm shadow-gray-950/5 dark:shadow-black/20",
        className
      )}
      {...props}
    >
      <UIBox className={cn("p-4", itemSpacing && "space-y-2")}>
        {children}
      </UIBox>
    </UIScrollArea>
  )
})

UIScrollAreaList.displayName = "UIScrollAreaList"

// ============================================================================
// Utility Hook for Scroll Position
// ============================================================================

export function useScrollPosition(elementRef: React.RefObject<HTMLElement>) {
  const [scrollPosition, setScrollPosition] = React.useState({ x: 0, y: 0 })
  const [isAtTop, setIsAtTop] = React.useState(true)
  const [isAtBottom, setIsAtBottom] = React.useState(false)

  React.useEffect(() => {
    const element = elementRef.current?.querySelector('[data-slot="scroll-area-viewport"]') as HTMLElement
    if (!element) return

    const handleScroll = () => {
      const { scrollTop, scrollLeft, scrollHeight, clientHeight } = element
      setScrollPosition({ x: scrollLeft, y: scrollTop })
      setIsAtTop(scrollTop === 0)
      setIsAtBottom(scrollTop + clientHeight >= scrollHeight - 1)
    }

    element.addEventListener('scroll', handleScroll)
    handleScroll() // Initial check

    return () => element.removeEventListener('scroll', handleScroll)
  }, [elementRef])

  return {
    scrollPosition,
    isAtTop,
    isAtBottom,
    scrollTo: (position: { x?: number; y?: number }) => {
      const element = elementRef.current?.querySelector('[data-slot="scroll-area-viewport"]') as HTMLElement
      if (element) {
        if (position.x !== undefined) element.scrollLeft = position.x
        if (position.y !== undefined) element.scrollTop = position.y
      }
    },
    scrollToTop: () => {
      const element = elementRef.current?.querySelector('[data-slot="scroll-area-viewport"]') as HTMLElement
      if (element) element.scrollTop = 0
    },
    scrollToBottom: () => {
      const element = elementRef.current?.querySelector('[data-slot="scroll-area-viewport"]') as HTMLElement
      if (element) element.scrollTop = element.scrollHeight
    }
  }
}

// ============================================================================
// Type Re-exports
// ============================================================================

